'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';

export interface OwnedScheduleDirectoryOptions<TSchedule> {
  ownerId: string | null;
  routeKey: string;
  readSchedules: (ownerId: string) => Promise<TSchedule[]>;
  readPrimaryId: (ownerId: string) => Promise<string | null>;
}

interface Context { ownerId: string | null; routeKey: string }
interface DirectoryState<TSchedule> extends Context {
  schedules: TSchedule[];
  primaryScheduleId: string | null;
  isLoading: boolean;
  error: string | null;
}

const sameContext = (first: Context, second: Context) => first.ownerId === second.ownerId && first.routeKey === second.routeKey;
// An owned directory is unknown until its first explicit refresh settles.
const emptyDirectory = <TSchedule,>(context: Context): DirectoryState<TSchedule> => ({ ...context, schedules: [], primaryScheduleId: null, isLoading: Boolean(context.ownerId), error: null });

/** Explicit refreshes only. Owner, route, mount lifetime, and latest request own publication. */
export function useOwnedScheduleDirectory<TSchedule>({ ownerId, routeKey, readSchedules, readPrimaryId }: OwnedScheduleDirectoryOptions<TSchedule>) {
  const renderContext = useMemo<Context>(() => ({ ownerId, routeKey }), [ownerId, routeKey]);
  const [state, setState] = useState<DirectoryState<TSchedule>>(() => emptyDirectory<TSchedule>({ ownerId, routeKey }));
  const contextRef = useRef<Context | null>(null);
  const requestRef = useRef<symbol | null>(null);
  const readersRef = useRef<Pick<OwnedScheduleDirectoryOptions<TSchedule>, 'readSchedules' | 'readPrimaryId'> | null>(null);

  useLayoutEffect(() => { readersRef.current = { readSchedules, readPrimaryId }; });
  useLayoutEffect(() => {
    // A fresh object is the committed mount/route lifetime token, including a
    // return to the same owner and route after an intervening replacement.
    const context = renderContext;
    contextRef.current = context;
    requestRef.current = null;
    setState(previous => sameContext(previous, context) ? previous : emptyDirectory<TSchedule>(context));
    return () => {
      if (contextRef.current !== context) return;
      contextRef.current = null;
      requestRef.current = null;
    };
  }, [renderContext]);

  const refresh = useCallback(async (): Promise<boolean> => {
    const context = contextRef.current;
    const readers = readersRef.current;
    if (!context?.ownerId || !readers) return false;
    const requestedOwnerId = context.ownerId;
    const request = Symbol('owned-schedule-directory');
    requestRef.current = request;
    const isCurrent = () => contextRef.current === context && requestRef.current === request;
    setState(previous => isCurrent() ? { ...(sameContext(previous, context) ? previous : emptyDirectory<TSchedule>(context)), isLoading: true, error: null } : previous);

    try {
      // Both reads start independently, including when either reader throws
      // synchronously. Neither partial response is published on its own.
      const [schedules, primaryScheduleId] = await Promise.all([
        Promise.resolve().then(() => readers.readSchedules(requestedOwnerId)),
        Promise.resolve().then(() => readers.readPrimaryId(requestedOwnerId)),
      ]);
      if (!isCurrent()) return false;
      setState(previous => isCurrent() ? { ...context, schedules, primaryScheduleId, isLoading: false, error: null } : previous);
      return true;
    } catch (reason) {
      if (!isCurrent()) return false;
      const error = reason instanceof Error && reason.message ? reason.message : 'Schedules could not be loaded.';
      setState(previous => isCurrent() ? { ...(sameContext(previous, context) ? previous : emptyDirectory<TSchedule>(context)), isLoading: false, error } : previous);
      throw reason;
    }
  }, []);

  const setSchedules: Dispatch<SetStateAction<TSchedule[]>> = useCallback((action: SetStateAction<TSchedule[]>) => {
    const context = contextRef.current;
    if (!context?.ownerId || context !== renderContext) return;
    setState(previous => {
      if (contextRef.current !== context) return previous;
      const current = sameContext(previous, context) ? previous : emptyDirectory<TSchedule>(context);
      return { ...current, schedules: typeof action === 'function' ? action(current.schedules) : action };
    });
  }, [renderContext]);

  const setPrimaryScheduleId: Dispatch<SetStateAction<string | null>> = useCallback((action: SetStateAction<string | null>) => {
    const context = contextRef.current;
    if (!context?.ownerId || context !== renderContext) return;
    setState(previous => {
      if (contextRef.current !== context) return previous;
      const current = sameContext(previous, context) ? previous : emptyDirectory<TSchedule>(context);
      return { ...current, primaryScheduleId: typeof action === 'function' ? action(current.primaryScheduleId) : action };
    });
  }, [renderContext]);

  // Render-time projection also hides private rows on the first render of a
  // different owner/route, before the layout reset commits the empty state.
  const visible = sameContext(state, { ownerId, routeKey }) ? state : emptyDirectory<TSchedule>({ ownerId, routeKey });
  return { schedules: visible.schedules, setSchedules, primaryScheduleId: visible.primaryScheduleId, setPrimaryScheduleId, refresh, isLoading: visible.isLoading, error: visible.error };
}
